import { FormState, FORM_ERROR } from "final-form"
import { useState } from "preact/hooks"
import { Field, Form, FormRenderProps, FormSpy } from "react-final-form"
import classNames from "utils/preact/class-names"

import { trackLogin } from "../services/analytics"
import { getCsrfToken } from "../services/backend"
import { Screens } from "../services/screens"
import { profile } from "../services/store"
import { AuthProviders } from "../services/support"
import { useTranslation } from "../components/Translation"
import { SubscriptionButtons } from "../components/SubscriptionButtons"
import { Sidebar } from "../components/Sidebar"
import appleLogo from "../../../static/logos/apple-logo--black.svg"
import googleLogo from "../../../static/logos/google-g-logo.svg"
import eyeSlash from "../static/eye-slash.svg"
import eye from "../static/eye.svg"
import css from "../style.module.scss"

interface FormValues {
  email: string
  password: string
}

export function SignIn({
  token,
  authProviders,
  onAuthenticate,
  onPasswordForgot,
  onPageUpdate,
}: {
  token: string
  authProviders?: AuthProviders
  onAuthenticate: () => void
  onPasswordForgot: () => void
  onPageUpdate: (page: Screens) => void
}) {
  const t = useTranslation()
  const [passwordHidden, setPasswordHidden] = useState(true)
  const appleProvider = authProviders?.apple
  const googleProvider = authProviders?.google
  const appleEnabled = Boolean(appleProvider?.enabled)
  const googleEnabled = Boolean(googleProvider?.enabled)
  const hasSocialAuth = appleEnabled || googleEnabled

  return (
    <div className={css.content}>
      <div className={css["split-container"]}>
        <div className={css["flex-grow"]}>
          <Sidebar />
          <SubscriptionButtons />
        </div>
        <div className={css["horizontal-divider"]} />
        <div className={css["flex-grow"]}>
          <h3 className={classNames({ [css["form-title"]]: true, [css["mb-2"]]: true })}>
            {t("log_in")}
          </h3>

          {hasSocialAuth && (
            <div className={css["social-auth-buttons"]}>
              {appleEnabled && (
                <>
                  <button
                    id="custom-apple-signin"
                    type="button"
                    className="apple-sign-in__large--white"
                    data-client-id={appleProvider?.client_id || ""}
                    data-redirect-uri={appleProvider?.redirect_uri || ""}
                    data-state={appleProvider?.state || ""}
                    data-nonce={appleProvider?.nonce || ""}
                  >
                    <img
                      src={appleLogo}
                      alt="Apple"
                      className="apple-sign-in__large--white__logo"
                    />
                    Sign In with Apple
                  </button>
                  <div
                    id="appleid-signin"
                    style={{ display: "none" }}
                    data-color="black"
                    data-border="true"
                    data-border-radius="4"
                    data-type="sign in"
                  />
                </>
              )}
              {googleEnabled && (
                <>
                  <button
                    id="custom-google-signin"
                    type="button"
                    className="google-sign-in__large--white"
                    data-client-id={googleProvider?.client_id || ""}
                    data-nonce={googleProvider?.nonce || ""}
                    tabIndex={0}
                  >
                    <img
                      src={googleLogo}
                      alt="Google"
                      className="google-sign-in__large--white__logo"
                    />
                    Sign In with Google
                  </button>
                  <div id="googleid-signin" style={{ display: "none" }} />
                </>
              )}
            </div>
          )}
          {hasSocialAuth && (
            <div className={css["login-divider"]}>
              <hr />
              {t("or")}
              <hr />
            </div>
          )}

          <Form
            onSubmit={(values: FormValues): Promise<Object | undefined> =>
              fetch("/session", {
                method: "post",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                  "X-CSRF-Token": getCsrfToken(token),
                },
                credentials: "include",
                body: JSON.stringify(values),
              })
                .then((res) => {
                  if (!res.ok) {
                    if (res.status !== 422 && res.status !== 401) {
                      return {
                        [FORM_ERROR]: `${res.status}: ${res.statusText}`,
                      }
                    }
                  }

                  return res.json()
                })
                .then((res) => {
                  if (res.user) {
                    profile.setKey("emailVerified", res.user.emailVerified)
                    profile.setKey("premium", res.user.premium)
                    profile.setKey("email", res.user.email)
                  }

                  if (res.success) return undefined
                  if (res.errors) return res.errors
                  return res
                })
                .catch((err: Error) => ({
                  [FORM_ERROR]: err.message,
                }))
            }
          >
            {({
              values,
              hasSubmitErrors,
              submitErrors,
              submitError,
              submitting,
              handleSubmit,
            }: FormRenderProps & FormState<FormValues>) => (
              <form action="/session" method="post" onSubmit={handleSubmit}>
                <FormSpy
                  subscription={{ submitSucceeded: true }}
                  onChange={({
                    submitSucceeded,
                  }: {
                    values: FormValues
                    submitSucceeded: boolean
                  }) => {
                    if (submitSucceeded) {
                      profile.setKey("authorized", true)
                      profile.setKey("reloadRequired", true)
                      trackLogin()
                      onAuthenticate()
                    }
                  }}
                />
                <Field
                  name="email"
                  render={({ input, meta }) => (
                    <label
                      className={classNames({
                        [css.label]: true,
                        [css["is-invalid"]]: Boolean(meta.submitError),
                      })}
                      htmlFor="email"
                    >
                      {t("email")}
                      <span className={css.error}>
                        {meta.submitError && `(${meta.submitError})`}
                      </span>
                      <div
                        className={classNames({
                          [css["input-wrapper"]]: true,
                          [css["is-invalid"]]: Boolean(meta.submitError),
                        })}
                      >
                        <input
                          {...input}
                          id="email"
                          type="email"
                          autoComplete="email"
                          className={classNames({
                            [css.input]: true,
                            [css["is-invalid"]]: Boolean(meta.submitError),
                          })}
                        />
                      </div>
                    </label>
                  )}
                />
                <Field
                  name="password"
                  render={({ input, meta }) => (
                    <label
                      className={classNames({
                        [css.label]: true,
                        [css["is-invalid"]]: Boolean(meta.submitError),
                      })}
                      htmlFor="password"
                    >
                      {t("password")}
                      <span className={css.error}>
                        {meta.submitError && `(${meta.submitError})`}
                      </span>
                      <div className={css["password-field-label"]}>
                        <button
                          type="button"
                          className={css["password-field-toggle"]}
                          onClick={() => setPasswordHidden((hidden) => !hidden)}
                        >
                          <img
                            src={passwordHidden ? eye : eyeSlash}
                            alt={passwordHidden ? "Show password" : "Hide password"}
                          />
                        </button>
                      </div>
                      <div
                        className={classNames({
                          [css["input-wrapper"]]: true,
                          [css["is-invalid"]]: Boolean(meta.submitError),
                        })}
                      >
                        <input
                          {...input}
                          id="password"
                          type={passwordHidden ? "password" : "text"}
                          autoComplete="current-password"
                          className={classNames({
                            [css.input]: true,
                            [css["is-invalid"]]: Boolean(meta.submitError),
                          })}
                        />
                      </div>
                      <a
                        className={classNames({
                          [css["text-highlight"]]: true,
                          [css["font-regular"]]: true,
                          [css["text-underline"]]: true,
                        })}
                        href="/passwords/new"
                        onClick={(e) => {
                          e.preventDefault()
                          onPasswordForgot()
                        }}
                      >
                        {t("forgot_password")}
                      </a>
                    </label>
                  )}
                />

                {hasSubmitErrors && (
                  <p
                    className={classNames({
                      [css["text-medium"]]: true,
                      [css["error-highlight"]]: true,
                    })}
                  >
                    {submitError || submitErrors}
                  </p>
                )}

                <div className={css["spacer-bottom"]} />

                <button
                  className={classNames({
                    [css.submit]: true,
                    [css["is-loading"]]: submitting,
                  })}
                  type="submit"
                  disabled={submitting || !values.email || !values.password}
                >
                  {t("continue")}
                </button>
              </form>
            )}
          </Form>

          <p className={css["terms-text"]}>
            {t("agree_to_terms")}{" "}
            <a href="/pages/terms" target="_blank" rel="noopener noreferrer">
              Terms & Services
            </a>
          </p>

          <p className={css["no-account-text"]}>
            {t("no_account")}{" "}
            <a
              className={classNames({
                [css["text-highlight"]]: true,
                [css["text-underline"]]: true,
              })}
              href="/sign_up"
              onClick={(e) => {
                e.preventDefault()
                onPageUpdate(Screens.SignUpScreen)
              }}
            >
              {t("join_now")}
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
